// Compact list-row for the signup approval queue.
// Spec: docs/spec/core/part2-product-requirements.md §13.3 Req 615.
// Spec: docs/spec/topics/onboarding/approval-queue.md "Left pane — list".

interface SignupRequestRowData {
  id: string;
  email: string;
  emailDomain: string;
  name: string | null;
  company: string | null;
  role: string | null;
  status: string;
  captchaPassed: boolean;
  createdAt: Date | string;
  approvedAt: Date | string | null;
  rejectedAt: Date | string | null;
  createdTenantId: string | null;
}

interface SignupRequestRowProps {
  request: SignupRequestRowData;
  isSelected: boolean;
  onSelect: () => void;
}

function relativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const ms = Date.now() - d.getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const STATUS_BADGE_STYLES: Record<string, { bg: string; fg: string; label: string }> = {
  pending: { bg: '#f59e0b22', fg: '#f59e0b', label: 'Pending' },
  approved: { bg: '#10b98122', fg: '#10b981', label: 'Approved' },
  rejected: { bg: '#6b728022', fg: '#9ca3af', label: 'Rejected' },
};

export function SignupRequestRow({ request, isSelected, onSelect }: SignupRequestRowProps) {
  const company = request.company?.trim() || '(personal email)';
  const submitter = request.name?.trim() || request.email;
  const badge = STATUS_BADGE_STYLES[request.status] ?? { bg: '#33333333', fg: '#999', label: request.status };

  return (
    <button
      onClick={onSelect}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '12px 16px',
        border: 'none',
        borderBottom: '1px solid var(--color-border, #2a2a2a)',
        background: isSelected ? 'var(--color-bg-selected, #1f2937)' : 'transparent',
        cursor: 'pointer',
        color: 'inherit',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{company}</span>
        <span
          style={{
            fontSize: 11,
            background: badge.bg,
            color: badge.fg,
            padding: '2px 8px',
            borderRadius: 999,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {badge.label}
        </span>
      </div>
      <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted, #888)' }}>
        {submitter}
      </div>
      <div style={{ marginTop: 4, fontSize: 12, color: 'var(--color-text-muted, #888)' }}>
        {request.role ? `${request.role} · ` : ''}
        {relativeTime(request.createdAt)}
      </div>
    </button>
  );
}
